import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { LoadingMochi } from '@autopass-web/mochi-component';
import { axiosAutoPassErrorConverter } from '@autopass-web/mochi-utils';

import { environment } from '../../environments/environment';
import { ZERO_AMOUNT } from '../leave-page';
import { AlprPaymentView, useAlprPaymentViewStore } from './alpr-payment-view';
import {
  NewUserDiscountDialog,
  PaymentSettingDialog,
  useNewUserDiscountDialogStore,
  usePaymentSettingDialogStore,
} from './dialogs';
import { getPlateNumberInfo, useParkingLotPaymentInfo } from './hooks';
import styles from './index.module.scss';

import type { ParkingLotInfo, PlateNumberInfo } from './hooks';
import type { AutopassAxiosError } from '@autopass-web/mochi-utils';

import { PageContainer } from '#/guest-aps/components/page-container';
import { ERROR_CODES, PATH } from '#/guest-aps/config';
import { ParkingRecordList } from '#/guest-aps/features/select-plate-number/parking-record-list';
import { useAmplitudeSelectPlateNumberPageView } from '#/guest-aps/hooks/use-amplitude-select-plate-number-page-view';
import amplitude from '#/guest-aps/services/amplitude';
import {
  localStorageController,
  sessionStorageController,
} from '#/guest-aps/services/storages';
import { useParkingTicketCreate } from '#/guest-aps/services/use-parking-ticket-create';

type PaymentOption =
  | 'WEB_PAYMENT' // 傳統繳費機線上化付款
  | 'NEW_USER_DISCOUNT_REQUEST' // 新用戶申辦折扣 dialog
  | 'PAYMENT_SETTING_REQUEST' // 設定付款方式 dialog
  | 'ALPR'; // 車辯離場

const getPaymentFlowType = (
  plateNumberInfo: PlateNumberInfo,
  parkingLotPaymentInfo: ParkingLotInfo,
): PaymentOption => {
  if (!parkingLotPaymentInfo.support_kiosk_to_alpr) {
    return 'WEB_PAYMENT';
  }

  // no members info means not member
  if (
    plateNumberInfo.members_info.length < 1 &&
    parkingLotPaymentInfo.allow_switch_to_autopass_member
  ) {
    return 'NEW_USER_DISCOUNT_REQUEST';
  }

  const memberAlprAllowed = plateNumberInfo.members_info.filter((i) => {
    return i.enable_alpr && i.has_payment_method;
  });

  if (memberAlprAllowed.length > 0) {
    return 'ALPR';
  }

  if (!parkingLotPaymentInfo.allow_switch_to_autopass_member) {
    return 'WEB_PAYMENT';
  }

  const AUTOPASS_DISTRIBUTOR_NAME = '車麻吉';

  const autopassMembers = plateNumberInfo.members_info.filter((i) => {
    return i.is_autopass_member;
  });

  if (autopassMembers.length < 1) {
    return 'WEB_PAYMENT';
  }

  const memberNeedSetPayment = autopassMembers.filter((i) => {
    return (
      i.distributor_name === AUTOPASS_DISTRIBUTOR_NAME && !i.has_payment_method
    );
  });

  return memberNeedSetPayment.length > 0
    ? 'PAYMENT_SETTING_REQUEST'
    : 'WEB_PAYMENT';
};

export const SelectPlateNumberPage = () => {
  const parkingRecordBills = useMemo(
    () => sessionStorageController.getPlateNumberSearchResult() ?? [],
    [],
  );
  const plateNumberFuzzy = localStorageController.getPlateNumberFuzzy();
  const apsPartnerTemplate = sessionStorageController.getApsPartner();

  useAmplitudeSelectPlateNumberPageView(parkingRecordBills);

  const navigate = useNavigate();
  const { createParkingTicket, createParkingTicketStatus } =
    useParkingTicketCreate();

  // Use plateNumber as a identifier instead of id
  // In production, id is not generated by our server. It might not be unique whereas plateNumber will always an unique identifier.
  const { onDisplay: onPaymentSettingDialogDisplay } =
    usePaymentSettingDialogStore();

  const { onDisplay: onNewUserDiscountDialogDisplay } =
    useNewUserDiscountDialogStore();

  const { onDisplay: onAlprPaymentViewDisplay } = useAlprPaymentViewStore();

  const parkinglotInfo = sessionStorageController.getParkinglot();
  const { data: parkingLotPaymentInfo } = useParkingLotPaymentInfo(
    {
      id: parkinglotInfo?.id,
    },
    { enabled: !!parkinglotInfo?.id },
  );

  const partnerSupportText = apsPartnerTemplate?.attributes.support_text;

  const payWithWeb = async (plateNumber: string) => {
    if (createParkingTicketStatus === 'pending') return;

    const bill = parkingRecordBills.find(
      (bill) => bill.plate_number === plateNumber,
    );
    if (bill == null) return;

    const {
      id: parkingRecordBillId,
      vehicle_type,
      parking_record_kiosk_id,
      plate_number,
    } = bill;

    try {
      const { data } = await createParkingTicket({
        parkingLotId: parkinglotInfo?.id,
        vehicleType: vehicle_type,
        plateNumber: plate_number,
        parkingRecordId: parkingRecordBillId,
        parkingRecordKioskId: parking_record_kiosk_id,
      });

      const {
        data: { request_amount, id: parkingTicketId },
      } = data;

      // If request_amount is 0, navigate to leave page
      if (Math.round(+request_amount) === 0) {
        amplitude.leavePageView('已成功繳費', parkingTicketId);
        navigate(`${PATH.LEAVE}?code=${ZERO_AMOUNT}`, { replace: true });
        return;
      }

      navigate(PATH.PAYMENT);
    } catch (error) {
      amplitude.selectPlateNumberErrorPageView(plate_number, error);
      const result = axiosAutoPassErrorConverter(error as AutopassAxiosError);

      switch (result.code) {
        case 'parking_record_not_found':
        case 'parkinglot_server_error':
        case 'parkinglot_server_record_not_found':
        case 'parking_ticket_save_error':
          return navigate(`${PATH.ERROR}?code=${ERROR_CODES.NO_EXIST_RECORD}`);
        case 'parkinglot_server_vehicle_type_error':
          return navigate(`${PATH.ERROR}?code=${ERROR_CODES.PARKINGLOT_ERROR}`);
        case 'parkinglot_setting_error':
          return navigate(
            `${PATH.ERROR}?code=${ERROR_CODES.PARKINGLOT_SETTING_ERROR}`,
          );
        default:
          return navigate(PATH.ERROR);
      }
    }
  };

  const getTicketAmount = async (plateNumber: string) => {
    if (createParkingTicketStatus === 'pending') return;

    const bill = parkingRecordBills.find(
      (bill) => bill.plate_number === plateNumber,
    );
    if (bill == null) return;

    const {
      id: parkingRecordBillId,
      vehicle_type,
      parking_record_kiosk_id,
      plate_number,
    } = bill;

    try {
      const { data } = await createParkingTicket({
        parkingLotId: parkinglotInfo?.id,
        vehicleType: vehicle_type,
        plateNumber: plate_number,
        parkingRecordId: parkingRecordBillId,
        parkingRecordKioskId: parking_record_kiosk_id,
      });

      return data.data.request_amount;
    } catch {
      // only get amount here, so no error handling here

      return Promise.reject();
    }
  };

  const handlePlateRecordListItemClick = async (plateNumber: string) => {
    const bill = parkingRecordBills.find(
      (bill) => bill.plate_number === plateNumber,
    );

    if (bill == null) return;

    const { store_is_support_alpr, checked_in_image_url } = bill;

    const orderOfCurrentBill =
      parkingRecordBills.findIndex((b) => b === bill) + 1;

    const isDisplayImage =
      store_is_support_alpr && checked_in_image_url ? 'Y' : 'N';

    sessionStorageController.saveSelectedParkingBill(bill);

    amplitude.setUserId(bill.plate_number);

    try {
      const info = await getPlateNumberInfo({ plate_number: plateNumber });

      const paymentFlow = getPaymentFlowType(
        info.data.data,
        parkingLotPaymentInfo,
      );

      amplitude.selectPlateNumberOnClick({
        plateNumber,
        order: orderOfCurrentBill,
        isDisplayImage,

        ...{ ...info.data.data, parkingLotPaymentInfo, paymentFlow },
      });

      switch (paymentFlow) {
        case 'ALPR':
          {
            const amount = await getTicketAmount(plateNumber);

            onAlprPaymentViewDisplay({
              plateNumber,
              priceAmountStr: String(Math.round(+amount)),
              parkingRecordId: bill.id,
              onClick: () => {
                payWithWeb(plateNumber);
              },
            });
          }

          return;
        case 'NEW_USER_DISCOUNT_REQUEST': {
          const onSubmit = async () => {
            window.location.href = environment.newPlateLink;
          };

          const onCancel = () => {
            payWithWeb(plateNumber);
          };

          onNewUserDiscountDialogDisplay({
            onSubmit,
            onCancel,
            parkingRecordId: String(orderOfCurrentBill),
            plateNumber,
          });
          return;
        }

        case 'PAYMENT_SETTING_REQUEST': {
          const onSubmit = async () => {
            window.location.href = environment.paymentBindingLink;
          };

          const onCancel = () => {
            payWithWeb(plateNumber);
          };

          onPaymentSettingDialogDisplay({
            onSubmit,
            onCancel,
            parkingRecordId: bill.id,
            plateNumber,
          });
          return;
        }
        default:
          payWithWeb(plateNumber);
          return;
      }
    } catch (err) {
      console.log(err);

      payWithWeb(plateNumber);
    }
  };

  return (
    <>
      <NewUserDiscountDialog />
      <PaymentSettingDialog />
      <AlprPaymentView />
      <LoadingMochi show={createParkingTicketStatus === 'pending'} />
      <PageContainer>
        <div className={styles.wrapper}>
          <div className={styles.title}>選擇繳費車輛</div>
          <div className={styles.desc}>
            查詢「{plateNumberFuzzy}」相近的車牌結果：
          </div>
          <div className={styles.hint}>
            {partnerSupportText ?? '※若無相符車輛請找停車場管理員協助。'}
          </div>
          <ParkingRecordList
            parkingRecordBills={parkingRecordBills}
            onItemClick={handlePlateRecordListItemClick}
          />
        </div>
      </PageContainer>
    </>
  );
};
